import * as React from "react";
import { graphql, Link, type HeadFC, type PageProps } from "gatsby";
import HeaderFooter from "@/layouts/header-footer";
import { Display, P, PL, PS, H4 } from "@/components/ui/typography";
import { GatsbyImage } from "gatsby-plugin-image";
import AvatarDetails from "@/components/avatar-detail";
import { container } from "@/styles/common-styles.module.css";
import LottieAnimation from "@/components/lottie";
import AnimationHerosectionDesktop from "@animations/blog-listing/blog-listing-desktop.json";
import AnimationHerosectionMobile from "@animations/blog-listing/blog-listing-mobile.json";
import HomepageCta from "@/components/cta/home-page-cta";
import BlogList, { BlogListType } from "@/components/blog/blog-list";
import { BlogPost } from "@/templates/blog-post";
import Section from "@/layouts/section";
import BlogHeader from "@/components/blog/blog-header";
import Button from "@/components/ui/button";
import { blogContainer, paginationContainer } from "./template.module.css";
import { kebabCase } from "@/utils";

interface BlogListAndFeaturedBlog extends BlogListType {
  strapiFeaturedBlog: {
    featuredBlog: BlogPost;
  };
}

interface PageContext {
  currentPage: number;
  numPages: number;
  tag: string;
}
interface categoryCtaData {
  ctaTitle: string;
  ctaDescription: string;
  ctaBtnText: string;
}
export interface BlogCategoryCtaData {
  allStrapiSingleBlogPage: {
    nodes: categoryCtaData[];
  };
}
const BlogPage: React.FC<PageProps> = ({ data, pageContext }) => {
  const desktopAnimationStyle = {
    top: "-8vw"
  };
  const featuredBlog = (data as BlogListAndFeaturedBlog).strapiFeaturedBlog.featuredBlog;
  const blogPosts = (data as BlogListAndFeaturedBlog).allStrapiBlog.nodes || [];
  const blogPostCta = (data as BlogCategoryCtaData).allStrapiSingleBlogPage.nodes;
  const { numPages, currentPage, tag: tagName } = pageContext as PageContext;

  return (
    <HeaderFooter>
      <div className={`${container} md:mt-40`}>
        <div className="-mx-6 md:hidden mb-3">
          <LottieAnimation animationData={AnimationHerosectionMobile} />
        </div>
        <div className="w-full max-w-3xl relative mt-8 md:mt-0 z-[2]">
          <Display>Railsfactory Blog</Display>
          <PL className="mt-6 md:mt-8 max-w-2xl relative">
            The vibrant pulse of Ruby on Rails expertise.
          </PL>
        </div>
      </div>
      <div className="-mx-6 relative hidden md:flex z-0" style={desktopAnimationStyle}>
        <LottieAnimation animationData={AnimationHerosectionDesktop} className="flex-shirnk-0" />
      </div>

      <div className={`${container}`}>
        <Link to={`/blog/${featuredBlog.slug.slug}`} className="group">
          <div className="md:flex md:gap-20 mt-16 md:mt-0">
            <div className="overflow-hidden rounded-2xl md:w-1/2">
              <GatsbyImage
                alt={featuredBlog.heroImage?.alternativeText || "Hero image"}
                image={featuredBlog.heroImage.localFile.childImageSharp.gatsbyImageData}
                className="block group-hover:scale-125 transition"
              />
            </div>
            <div className="md:flex flex-row md:flex-col md:items-start md:justify-center md:w-2/4">
              <PS className="mt-6 md:mt-0 uppercase">
                {featuredBlog.tags.map((t) => t.tag).join(" ")}
              </PS>
              <H4 className="my-4 group-hover:text-primary">{featuredBlog.title}</H4>
              <P>{featuredBlog.description}</P>
              <AvatarDetails
                className="mt-6"
                avatar={featuredBlog.author.avatar.localFile.childImageSharp.gatsbyImageData}
                altText={featuredBlog.author.avatar.alternativeText}
                subText={`${featuredBlog.readTime} read`}
                name={featuredBlog.author.name}
              />
            </div>
          </div>
        </Link>
      </div>
      <Section className="mt-8 md:mt-16">
        <BlogHeader />
        <div className={blogContainer}>
          <div className={paginationContainer}>
            {currentPage > 1 && (
              <Link
                to={`/blog/category/${kebabCase(tagName)}/${currentPage === 2 ? "" : currentPage - 1}`}
              >
                <Button variant="outline" className="mt-16 md:mt-20 block mx-auto" size="lg">
                  Previous page
                </Button>
              </Link>
            )}
            {currentPage < numPages && (
              <Link to={`/blog/category/${kebabCase(tagName)}/${currentPage + 1}`}>
                <Button variant="outline" className="mt-16 md:mt-20 block mx-auto" size="lg">
                  Next page
                </Button>
              </Link>
            )}
          </div>
          <BlogList blogList={blogPosts} />
        </div>
      </Section>
      {blogPostCta.map((cta) => (
        <HomepageCta title={cta.ctaTitle} description={cta.ctaDescription} cta={cta.ctaBtnText} />
      ))}
    </HeaderFooter>
  );
};

export const query = graphql`
  query BlogList($skip: Int!, $limit: Int!, $tag: String) {
    allStrapiBlog(
      sort: { createdAt: DESC }
      limit: $limit
      skip: $skip
      filter: { tags: { elemMatch: { tag: { eq: $tag } } } }
    ) {
      nodes {
        id
        heroImage {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
          alternativeText
        }
        title
        readTime
        slug {
          slug
        }
        author {
          name
          avatar {
            localFile {
              childImageSharp {
                gatsbyImageData
              }
            }
            alternativeText
          }
        }
      }
    }
    strapiFeaturedBlog {
      featuredBlog {
        id
        title
        description
        heroImage {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
          alternativeText
        }
        tags {
          tag
        }
        slug {
          slug
        }
        author {
          avatar {
            localFile {
              childImageSharp {
                gatsbyImageData
              }
            }
            alternativeText
          }
          name
        }
        readTime
      }
    }
    allStrapiSingleBlogPage {
      nodes {
        ctaTitle
        ctaDescription
        ctaBtnText
      }
    }
  }
`;

export default BlogPage;

export const Head: HeadFC = () => <title>Blog</title>;
